import type { ImpedanceExec, ImpedanceResult, TIA, ADC } from './generated/ediphor';
import {
  Hold,
  Reference,
  cHANToJSON,
  cHANFromJSON,
  tIAToJSON,
  tIAFromJSON,
  aDCToJSON,
  aDCFromJSON,
  referenceToJSON,
  referenceFromJSON,
} from './generated/ediphor';
import logger from '@/lib/logger';

/**
 * Convert ImpedanceExec to JSON-safe object for backend storage
 * Converts enum values to strings for readability
 */
export function impedanceExecToJson(exec: ImpedanceExec): Record<string, unknown> {
  return {
    amplitudeMV: exec.amplitudeMV,
    rtiaOhm: tIAToJSON(exec.rtiaOhm),
    biasMV: exec.biasMV,
    adcGain: aDCToJSON(exec.adcGain),
    channels: exec.channels?.map((c) => cHANToJSON(c)),
    frequencyHz: exec.frequencyHz,
    frequencyAuto: exec.frequencyAuto,
    intervalMs: exec.intervalMs,
    settlePeriods: exec.settlePeriods,
    settleMs: exec.settleMs,
    periodsMin: exec.periodsMin,
    durationMinMs: exec.durationMinMs,
    frequencySnapPct: exec.frequencySnapPct,
    reference: referenceToJSON(exec.reference),
    hold: exec.hold ? Hold.toJSON(exec.hold) : exec.hold,
  };
}

/**
 * Convert JSON object back to ImpedanceExec
 * Handles both string and numeric enum values
 */
export function jsonToImpedanceExec(json: Record<string, unknown>): ImpedanceExec {
  // TEMP DIAGNOSTIC — remove once confirmed working
  logger.debug('[jsonToImpedanceExec] raw input keys:', Object.keys(json), 'sample values:', {
    amplitudeMV: json.amplitudeMV, amplitudeMv: json.amplitudeMv,
    rtiaOhm: json.rtiaOhm, adcGain: json.adcGain,
    channels: json.channels,
    frequencyAuto: json.frequencyAuto,
  });
  // The backend serializes with camelCase: amplitudeMv / biasMv (lowercase 'v').
  // The protobuf TS type uses amplitudeMV / biasMV (uppercase 'V').
  // Accept both casings so this works regardless of backend serialization policy.
  // amplitudeMV/biasMV/intervalMs are all arrays (repeated numeric in proto). Not-yet-migrated
  // backend rows (Story 1.7) may still send a bare scalar for amplitudeMV/biasMV specifically
  // (they predate the array migration) — wrap it defensively. intervalMs is a new field with
  // no such legacy shape, but the same guard is cheap insurance against a malformed value.
  const toArray = (value: unknown): number[] => {
    if (Array.isArray(value)) return value.filter((v): v is number => typeof v === 'number');
    if (typeof value === 'number') return [value];
    if (value !== undefined && value !== null) {
      logger.debug('[jsonToImpedanceExec] unexpected array-typed field shape, falling back to []:', value);
    }
    return [];
  };
  const amplitudeMV = (() => {
    const arr = toArray(json.amplitudeMV ?? json.amplitudeMv);
    return arr.length > 0 ? arr : [0];
  })();
  const biasMV = (() => {
    const arr = toArray(json.biasMV ?? json.biasMv);
    return arr.length > 0 ? arr : [0];
  })();

  // frequencyAuto is a Scaler whose protobuf field for the log/linear blend is `blend`,
  // but the backend C# DTO uses `transition`. Map either key to `blend`.
  let frequencyAuto: ImpedanceExec['frequencyAuto'];
  if (json.frequencyAuto && typeof json.frequencyAuto === 'object') {
    const fa = json.frequencyAuto as Record<string, unknown>;
    frequencyAuto = {
      start:  (fa.start  as number) ?? 0,
      end:    (fa.end    as number) ?? 0,
      count:  (fa.count  as number) ?? 0,
      // 'blend' is the proto field; backend may send 'transition'
      blend:  (fa.blend  as number | undefined) ?? (fa.transition as number | undefined) ?? 0,
    };
  }

  // Placeholder fallback defaults pending firmware/domain sign-off (Story 1.6) — not tuned
  // values. Mirrors the amplitude/bias not-yet-migrated defensive pattern above: a saved
  // profile predating v1.4.4 won't have these keys, so absence falls back to a safe default
  // rather than failing the whole conversion. periodsMin/durationMinMs fall back to 1 (their
  // proto minimum) since 0 is out of range for those two specifically — clampToMin1 also
  // repairs an explicit 0/negative/non-numeric value rather than only a missing key, since
  // proto3 zero-omission would otherwise make an invalid 0 indistinguishable on the wire from
  // "field not sent".
  const clampToMin1 = (value: unknown): number => {
    const n = typeof value === 'number' ? value : NaN;
    return Number.isFinite(n) && n >= 1 ? n : 1;
  };
  const hold: Hold | undefined =
    json.hold && typeof json.hold === 'object' ? Hold.fromJSON(json.hold) : undefined;

  return {
    amplitudeMV,
    rtiaOhm: tIAFromJSON(json.rtiaOhm) as TIA,
    biasMV,
    adcGain: aDCFromJSON(json.adcGain) as ADC,
    channels: (json.channels as string[])?.map((c) => cHANFromJSON(c)) ?? [],
    frequencyHz: (json.frequencyHz as number[]) ?? [],
    frequencyAuto,
    intervalMs: toArray(json.intervalMs),
    settlePeriods: (json.settlePeriods as number) ?? 0,
    settleMs: (json.settleMs as number) ?? 0,
    periodsMin: clampToMin1(json.periodsMin),
    durationMinMs: clampToMin1(json.durationMinMs),
    frequencySnapPct: (json.frequencySnapPct as number) ?? 0,
    // Not-yet-migrated backend rows (predate this field) fall back to the proto's own
    // default enum value rather than the "not set" UNRECOGNIZED referenceFromJSON(undefined)
    // would otherwise produce.
    reference:
      json.reference !== undefined && json.reference !== null
        ? referenceFromJSON(json.reference)
        : Reference.BEFORE,
    hold,
  };
}

/**
 * Convert ImpedanceResult to JSON-safe object for backend storage.
 * `result.data` holds one entry per requested channel; each entry's arrays are
 * already per-channel (no de-interleaving needed) and share `result.frequencyHz`
 * as their common frequency axis.
 */
export function impedanceResultToJson(
  result: ImpedanceResult
): Record<string, unknown> {
  return {
    frequencyHz: result.frequencyHz,
    data: result.data?.map((d) => ({
      channel: cHANToJSON(d.channel),
      magnitudeOhm: d.magnitudeOhm,
      phaseRad: d.phaseRad,
      timeMs: d.timeMs,
      currentAcUA: d.currentAcUA,
      currentDcBeforeUA: d.currentDcBeforeUA,
      currentDcAfterUA: d.currentDcAfterUA,
      temperatureBeforeC: d.temperatureBeforeC,
      temperatureAfterC: d.temperatureAfterC,
      pointBitmask: d.pointBitmask,
    })),
  };
}

/**
 * Convert JSON object back to ImpedanceResult
 */
export function jsonToImpedanceResult(
  json: Record<string, unknown>
): ImpedanceResult {
  return {
    frequencyHz: (json.frequencyHz as number[]) ?? [],
    data: ((json.data as Array<Record<string, unknown>>) ?? []).map((d) => ({
      channel: cHANFromJSON(d.channel),
      magnitudeOhm: (d.magnitudeOhm as number[]) ?? [],
      phaseRad: (d.phaseRad as number[]) ?? [],
      timeMs: (d.timeMs as number[]) ?? [],
      currentAcUA: (d.currentAcUA as number[]) ?? [],
      currentDcBeforeUA: d.currentDcBeforeUA as number | undefined,
      currentDcAfterUA: d.currentDcAfterUA as number | undefined,
      temperatureBeforeC: d.temperatureBeforeC as number | undefined,
      temperatureAfterC: d.temperatureAfterC as number | undefined,
      pointBitmask: (d.pointBitmask as number[]) ?? [],
    })),
  };
}

/**
 * Serialize DevMessage response for logging or debugging
 */
export function serializeDevMessage(msg: unknown): string {
  return JSON.stringify(
    msg,
    (_, value) => {
      if (value instanceof Uint8Array) {
        return Array.from(value)
          .map((b) => b.toString(16).padStart(2, '0'))
          .join(':');
      }
      if (typeof value === 'bigint') {
        return value.toString();
      }
      return value;
    },
    2
  );
}
